import { useCallback, useMemo, useRef } from 'react';
import qs, { ParseOptions, StringifyOptions } from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';

const useURLState = <T>(
  init?: T,
  options?: ParseOptions &
    StringifyOptions & { parseNull?: boolean; shouldRefresh?: boolean },
) => {
  const {
    parseBooleans = true,
    parseNumbers = true,
    arrayFormat = 'index',
    skipEmptyString = true,
    skipNull = true,
    shouldRefresh = false,
  } = options || {};
  const initState = useRef<T | undefined>(init);
  const location = useLocation();
  const navigate = useNavigate();

  const targetQuery = qs.parse(location.search, {
    parseBooleans,
    parseNumbers,
    arrayFormat,
    ...options,
  }) as unknown as T;

  const updateState = {
    ...initState.current,
    ...targetQuery,
  };

  const memoState = useMemo(() => updateState, [location.search]);

  const setState = useCallback(
    (state: Partial<T>, opts?: StringifyOptions) => {
      navigate(
        {
          search: qs.stringify(state, {
            skipEmptyString,
            skipNull,
            arrayFormat,
            ...options,
            ...opts,
          }),
        },
        { replace: true },
      );
    },
    [location.pathname],
  );

  return [shouldRefresh ? updateState : memoState, setState] as const;
};

export default useURLState;
